import React, { InputHTMLAttributes, WheelEvent } from 'react';

import { TextInput, theme } from '@betterment/style-closet-react';
import styled from '@emotion/styled';
import { FieldValidator, useField } from 'formik';

export type NumberInputUnit = 'dollars' | 'dollarsPerMonth' | 'dollarsPerYear' | 'percent';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'inputMode' | 'size' | 'capture'> {
  name: string;
  unit?: NumberInputUnit;
  value?: string;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  mb?: string | number;
  fullWidth?: boolean;
}

const Container = styled.div<{ fullWidth: boolean }>`
  position: relative;
  max-width: ${props => (props.fullWidth ? '100%' : '40rem')};
`;

const Prefix = styled.div<{ disabled: boolean }>`
  position: absolute;
  padding-top: 1.2rem;
  padding-left: 1.6rem;
  top: 1px;
  line-height: 2.4rem;
  margin-bottom: 2rem;
  color: ${props => (props.disabled ? theme.colors.bmtGrey40 : theme.colors.bmtGrey60)};
`;

const Suffix = styled.div<{ disabled: boolean }>`
  position: absolute;
  padding-top: 1.2rem;
  padding-left: 1.6rem;
  top: 1px;
  right: 1.6rem;
  line-height: 2.4rem;
  margin-bottom: 2rem;
  color: ${props => (props.disabled ? theme.colors.bmtGrey40 : theme.colors.bmtGrey60)};
`;

// Do not use this line as an example. Instead of inline styles define components
// that make use of the style-closet-react library and '@emotion/styled' / styled-system
const UnitLeftPadding: { [Key in NumberInputUnit]: string | undefined } = {
  dollars: '2.6rem',
  dollarsPerMonth: '2.6rem',
  dollarsPerYear: '2.6rem',
  percent: undefined,
};

const UnitRightPadding: { [Key in NumberInputUnit]: string | undefined } = {
  dollars: undefined,
  dollarsPerMonth: '7.2rem',
  dollarsPerYear: '6rem',
  percent: '2.6rem',
};

function blurOnScroll(event: WheelEvent<HTMLInputElement>) {
  event.currentTarget.blur();
}

const NumberInput: React.FC<React.PropsWithChildren<InputProps>> = ({
  disabled = false,
  unit,
  invalid = false,
  fullWidth = false,
  ...rest
}) => (
  <Container fullWidth={fullWidth}>
    {(unit === 'dollars' || unit === 'dollarsPerMonth' || unit === 'dollarsPerYear') && (
      <Prefix disabled={disabled}>$</Prefix>
    )}

    <TextInput
      step="any"
      min="0"
      type="number"
      {...rest}
      px="0"
      pl={unit ? UnitLeftPadding[unit] : '6'}
      pr={unit ? UnitRightPadding[unit] : '6'}
      invalid={invalid}
      disabled={disabled}
      fullWidth={fullWidth}
      onWheel={blurOnScroll}
    />

    {unit === 'dollarsPerMonth' && <Suffix disabled={disabled}>/month</Suffix>}

    {unit === 'dollarsPerYear' && <Suffix disabled={disabled}>/year</Suffix>}

    {unit === 'percent' && <Suffix disabled={disabled}>%</Suffix>}
  </Container>
);

export default NumberInput;

export const FormikNumberInput: React.FC<React.PropsWithChildren<InputProps & { validate?: FieldValidator }>> = ({
  name,
  validate,
  onBlur: handleBlur,
  onChange: handleChange,
  ...rest
}) => {
  const [field] = useField({ name, validate });
  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    field.onChange(event);
    if (handleChange) {
      handleChange(event);
    }
  };
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(event);
    if (handleBlur) {
      handleBlur(event);
    }
  };
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ',') {
      event.preventDefault();
    }
  };
  return <NumberInput {...rest} {...field} onChange={onChange} onBlur={onBlur} onKeyDown={onKeyDown} />;
};
